// The Customers tab: one list with segments (who needs attention and why, who was
// lost, who is new or back, everyone), a panel for the chosen customer, and a call sheet.
import { useEffect, useMemo, useState } from "react";
import { getCustomerHealth } from "../../distflowAPI/salesAnalyticsApi";
import Answer from "./Answer";
import CallSheet from "./CallSheet";
import { customersAnswer } from "./answers";
import SplitView from "./SplitView";
import { daysAgo, plural, whyText } from "./customerText";
import { customerLevelFilters, hasItemLevelFilter } from "./drillDown";
import { changeText, count, materialTone, money, pct, shortDate, shortRange } from "./format";
import useSideReport from "./useSideReport";
import "./CustomerList.css";

export const CUSTOMER_LISTS = [
  { key: "attention", label: "Needs a call", source: "health" },
  { key: "late", label: "Stopped ordering", source: "health", statuses: ["late"], reason: true },
  { key: "switching", label: "Buying elsewhere", source: "health", statuses: ["switching"], reason: true },
  { key: "less", label: "Spending less", source: "health", statuses: ["spending_less", "ordering_less", "smaller_orders"], reason: true },
  { key: "lost", label: "Lost", source: "lost", comparison: true },
  { key: "new", label: "New", source: "new" },
  { key: "back", label: "Back again", source: "reactivated", comparison: true },
  { key: "all", label: "All customers", source: "all" },
];
const PAGE = 25;
const EMPTY = [];

function Name({ row, sub, openKey, onOpen }) {
  return <th scope="row" className="sa-col-name">
    <button type="button" className="sa-row-link" aria-current={openKey === row.key ? "true" : undefined}
      aria-controls="sa-detail-panel" onClick={(event) => { event.stopPropagation(); onOpen(row.key); }}>{row.name || "Unnamed customer"}</button>
    {sub && <small>{sub}</small>}
  </th>;
}

function columnsFor(source, { hasComparison, comparisonDates }) {
  if (source === "health") return ["Customer", "Salesman", "Why", "At stake a week"];
  if (source === "lost") return ["Customer", `Bought ${comparisonDates}`, "Orders then", "Last order"];
  if (source === "new") return ["Customer", "Sales", "Orders", "First order"];
  if (source === "reactivated") return ["Customer", "Sales", "Orders", "Customer since"];
  return ["Customer", "Sales", ...(hasComparison ? [comparisonDates, "Change"] : []), "Share"];
}

function Cells({ source, row, hasComparison, labels }) {
  const label = (index) => ({ "data-label": labels[index + 1] });
  if (source === "health") {
    const why = whyText(row);
    return <>
      <td className="sa-col-secondary sa-cust-rep" {...label(0)}>{row.salesman?.name || "Office"}</td>
      <td className="sa-cust-why sa-cell-wide sa-cell-plain" {...label(1)}><span className="sa-strong">{why.title}</span><small>{why.detail}</small></td>
      <td className="sa-num sa-cust-risk" {...label(2)}>{money(row.at_risk_weekly)}</td>
    </>;
  }
  if (source === "lost") {
    return <>
      <td className="sa-num" {...label(0)}>{money(row.previous_sales)}</td>
      <td className="sa-num" {...label(1)}>{count(row.previous_orders)}</td>
      <td className="sa-cust-when" {...label(2)}>{shortDate(row.last_order_date, { year: true })}<small>{daysAgo(row.days_since_last_order)}</small></td>
    </>;
  }
  if (source === "new" || source === "reactivated") {
    return <>
      <td className="sa-num" {...label(0)}>{money(row.sales)}</td>
      <td className="sa-num" {...label(1)}>{count(row.orders)}</td>
      <td className="sa-cust-when" {...label(2)}>{shortDate(row.first_order_date, { year: true })}</td>
    </>;
  }
  return <>
    <td className="sa-num" {...label(0)}>{money(row.sales)}</td>
    {hasComparison && <>
      <td className="sa-num" {...label(1)}>{money(row.previous_sales)}</td>
      <td className={`sa-num sa-delta sa-delta--${materialTone(row.change_amount, row.change_percent)}`} {...label(2)}>{changeText(row.change_amount, row.change_percent)}</td>
    </>}
    <td className="sa-num" {...label(hasComparison ? 3 : 1)}>{pct(row.share_percent)}</td>
  </>;
}

export default function CustomerList({ report, asOf, territoryId, filters = EMPTY, scope, list = "attention", onListChange,
  openKey, onOpen, renderPanel }) {
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const [attempt, setAttempt] = useState(0);
  // A print request: the rows to print. Cleared after printing or when the list changes,
  // so moving between lists never prints again.
  const [printing, setPrinting] = useState(null);

  const healthParams = useMemo(() => ({ as_of: asOf, territory_id: territoryId, filters: customerLevelFilters(filters) }),
    [asOf, territoryId, filters]);
  const health = useSideReport(getCustomerHealth, healthParams, Boolean(asOf), attempt);
  const customers = report?.customers;
  const hasComparison = Boolean(report?.comparison);
  const comparisonDates = hasComparison ? shortRange(report.comparison.start_date, report.comparison.end_date) : "";
  const lists = CUSTOMER_LISTS.filter((item) => !item.comparison || hasComparison);
  const current = lists.find((item) => item.key === list) || lists[0];
  const summary = health.data?.summary;
  const byStatus = summary?.by_status || {};
  const allRows = useMemo(() => (report?.breakdowns?.customer || []).filter((row) => row.key !== "adjustment"), [report]);

  const counts = {
    attention: summary?.flagged, late: byStatus.late, switching: byStatus.switching,
    less: summary ? (byStatus.spending_less || 0) + (byStatus.ordering_less || 0) + (byStatus.smaller_orders || 0) : undefined,
    lost: customers?.lost?.total, new: customers?.new?.total, back: customers?.reactivated?.total, all: allRows.length,
  };

  useEffect(() => { setVisible(PAGE); }, [list, query]);
  useEffect(() => { setPrinting(null); }, [list]);

  const rows = useMemo(() => {
    let source;
    if (current.source === "health") {
      source = (health.data?.rows || []).filter((row) => !current.statuses || current.statuses.includes(row.status));
    } else if (current.source === "all") source = allRows;
    else source = customers?.[current.source]?.rows || [];
    const term = query.trim().toLocaleLowerCase();
    if (!term) return source;
    return source.filter((row) => `${row.name || ""} ${row.salesman?.name || ""}`.toLocaleLowerCase().includes(term));
  }, [current, health.data, allRows, customers, query]);

  const isHealth = current.source === "health";
  const columns = columnsFor(current.source, { hasComparison, comparisonDates });
  const primary = lists.filter((item) => !item.reason);
  const primaryKey = current.reason ? "attention" : current.key;
  const reasons = lists.filter((item) => item.source === "health");
  const totalForList = current.source === "health" ? (current.key === "attention" ? health.data?.total : counts[current.key])
    : current.source === "all" ? allRows.length : customers?.[current.source]?.total;

  return <>
    <Answer answer={customersAnswer(health.data, report, asOf)} loading={health.loading} />
    {health.failed && <p className="sa-note" role="alert">The needs-a-call check could not be loaded.
      <button type="button" className="sa-link-button" onClick={() => setAttempt(attempt + 1)}>Try again</button></p>}
    <SplitView panel={openKey && renderPanel ? renderPanel : null}>
    <section className="sa-list" aria-label="Customers" aria-busy={health.loading}>
      <div className="sa-chips" role="group" aria-label="Customer lists">
        {primary.map((item) => <button type="button" key={item.key} aria-pressed={item.key === primaryKey}
          className={item.key === primaryKey ? "is-selected" : ""} onClick={() => onListChange?.(item.key)}>
          {item.label}<b>{counts[item.key] ?? "…"}</b>
        </button>)}
      </div>

      <div className="sa-tools">
        {isHealth && <label>Why
          <select value={current.key} onChange={(event) => onListChange?.(event.target.value)}>
            {reasons.map((item) => <option key={item.key} value={item.key}>{item.key === "attention" ? "Every reason" : item.label}{counts[item.key] != null ? ` (${count(counts[item.key])})` : ""}</option>)}
          </select>
        </label>}
        <input type="search" aria-label="Search customers" placeholder={isHealth ? "Search customers or salesmen" : "Search customers"}
          value={query} onChange={(event) => setQuery(event.target.value)} />
        {isHealth && rows.length > 0 && <button type="button" className="sa-outline-button" onClick={() => setPrinting({ id: Date.now(), title: current.label, rows })}>Print call sheet</button>}
      </div>
      {isHealth && hasItemLevelFilter(filters.map((key) => ({ dimension: String(key).split(":")[0] })))
        && <p className="sa-note">Route, vendor, category and product focus don’t apply to this list; it looks at each customer’s whole order.</p>}

      {rows.length > 0 ? <div className="sa-table-scroll"><table className="sa-list-table" aria-label={current.label}>
        <thead><tr>{columns.map((label, index) => <th scope="col" key={label}
          className={index === 0 ? "sa-col-name" : isHealth ? (index === 1 ? "sa-col-secondary" : index === 3 ? "sa-num" : "") : "sa-num"}>{label}</th>)}</tr></thead>
        <tbody>{rows.slice(0, visible).map((row) => <tr key={row.key} className={openKey === row.key ? "is-open" : ""} onClick={() => onOpen?.(row.key)}>
          <Name row={row} openKey={openKey} onOpen={(key) => onOpen?.(key)} />
          <Cells source={current.source} row={row} hasComparison={hasComparison} labels={columns} />
        </tr>)}</tbody>
      </table></div> : <p className="sa-list-empty">{health.loading && isHealth ? "Checking customers…"
        : query ? "No customers match this search."
          : isHealth ? "No regular customers in this list are behind their usual pattern."
            : `No customers in this list for these dates.`}</p>}

      <div className="sa-list-foot">
        <span>{rows.length > visible ? `Showing ${count(visible)} of ${count(rows.length)}${isHealth ? ", biggest first" : ""}.` : rows.length ? `${count(rows.length)} ${plural(rows.length, "customer")}.` : ""}
          {totalForList > rows.length && !query ? ` ${count(totalForList - rows.length)} more are left off the list.` : ""}
          {!isHealth && current.key === "lost" && rows.length ? ` Bought in ${comparisonDates}, nothing since.` : ""}</span>
        {rows.length > visible && <button type="button" className="sa-link-button" onClick={() => setVisible(visible + PAGE)}>Show {Math.min(PAGE, rows.length - visible)} more</button>}
      </div>
    </section>
    {printing && <CallSheet key={printing.id} title={printing.title} asOf={asOf} scope={scope} rows={printing.rows} onDone={() => setPrinting(null)} />}
  </SplitView>
  </>;
}
